import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  BarChart3, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, 
  XCircle, Target, Lightbulb, RefreshCw, ChevronRight, Activity,
  ArrowUp, ArrowDown, Minus, ArrowLeft, Shield
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAccuracyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [signalPerformance, setSignalPerformance] = useState(null);
  const [trends, setTrends] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, signalsRes, trendsRes, recsRes, feedbackRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/ai-accuracy/stats?days=${period}`),
        fetch(`${API_URL}/api/admin/ai-accuracy/signal-performance?days=${period}`),
        fetch(`${API_URL}/api/admin/ai-accuracy/trends?days=${period}`),
        fetch(`${API_URL}/api/admin/ai-accuracy/recommendations?days=${period}`),
        fetch(`${API_URL}/api/admin/ai-accuracy/recent-feedback?limit=20`)
      ]);

      const [statsData, signalsData, trendsData, recsData, feedbackData] = await Promise.all([
        statsRes.json(),
        signalsRes.json(),
        trendsRes.json(),
        recsRes.json(),
        feedbackRes.json()
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (signalsData.success) setSignalPerformance(signalsData);
      if (trendsData.success) setTrends(trendsData.trends);
      if (recsData.success) setRecommendations(recsData);
      if (feedbackData.success) setRecentFeedback(feedbackData.feedback);
    } catch (error) {
      console.error('Error fetching AI accuracy data:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      red: 'bg-red-50 text-red-600',
      amber: 'bg-amber-50 text-amber-600',
      purple: 'bg-purple-50 text-purple-600',
    };

    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">{title}</p>
              <p className="text-2xl font-bold mt-1">{value}</p>
              {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
            </div>
            <div className={`p-3 rounded-full ${colorClasses[color]}`}>
              <Icon className="h-6 w-6" />
            </div>
          </div>
          {trend !== undefined && (
            <div className="flex items-center mt-3 text-sm">
              {trend > 0 ? (
                <ArrowUp className="h-4 w-4 text-green-500 mr-1" />
              ) : trend < 0 ? (
                <ArrowDown className="h-4 w-4 text-red-500 mr-1" />
              ) : (
                <Minus className="h-4 w-4 text-gray-400 mr-1" />
              )}
              <span className={trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-600' : 'text-gray-500'}>
                {Math.abs(trend)}% vs last period
              </span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFeedbackTypeColor = (type) => {
    switch (type) {
      case 'correct': return 'bg-green-100 text-green-800';
      case 'false_positive': return 'bg-amber-100 text-amber-800';
      case 'false_negative': return 'bg-red-100 text-red-800';
      case 'threshold_issue': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const pieData = stats ? [
    { name: 'Correct', value: stats.correct, color: '#22c55e' },
    { name: 'False Positive', value: stats.false_positives, color: '#f59e0b' },
    { name: 'False Negative', value: stats.false_negatives, color: '#ef4444' },
    { name: 'Threshold Issue', value: stats.threshold_issues, color: '#3b82f6' },
  ].filter(d => d.value > 0) : [];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => window.location.href = '/admin/healthcare-moderation'}
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Moderation
              </Button>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-600" />
              AI Accuracy Dashboard
            </h1>
            <p className="text-gray-500 mt-1">Monitor and improve AI moderation performance</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/admin/healthcare-moderation'}
            >
              <Shield className="h-4 w-4 mr-2" />
              Healthcare Moderation
            </Button>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <StatCard
              title="Accuracy Rate"
              value={`${stats.accuracy_rate}%`}
              subtitle="Correct predictions"
              icon={Target}
              color={stats.accuracy_rate >= 80 ? 'green' : stats.accuracy_rate >= 60 ? 'amber' : 'red'}
            />
            <StatCard
              title="Total Predictions"
              value={stats.total_predictions}
              subtitle="In selected period"
              icon={Activity}
              color="blue"
            />
            <StatCard
              title="False Positives"
              value={stats.false_positives}
              subtitle={`${stats.false_positive_rate}% rate`}
              icon={AlertTriangle}
              color="amber"
            />
            <StatCard
              title="False Negatives"
              value={stats.false_negatives}
              subtitle={`${stats.false_negative_rate}% rate`}
              icon={XCircle}
              color="red"
            />
            <StatCard
              title="Override Rate"
              value={`${stats.override_rate}%`}
              subtitle="Admin overrides"
              icon={CheckCircle2}
              color="purple"
            />
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-4 lg:w-[500px]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="signals">Signals</TabsTrigger>
            <TabsTrigger value="recommendations">Improve</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Accuracy Trends Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Accuracy Trend</CardTitle>
                  <CardDescription>Daily accuracy over time</CardDescription>
                </CardHeader>
                <CardContent>
                  {trends && trends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                        <YAxis domain={[0, 100]} />
                        <Tooltip 
                          formatter={(value) => [`${value}%`, 'Accuracy']}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="accuracy" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          dot={{ fill: '#22c55e' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">
                      No trend data available yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Distribution Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Prediction Distribution</CardTitle>
                  <CardDescription>Breakdown by outcome type</CardDescription>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => [value, 'Count']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">
                      No distribution data yet
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Daily Breakdown */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Daily Breakdown</CardTitle>
                  <CardDescription>Predictions by type per day</CardDescription>
                </CardHeader>
                <CardContent>
                  {trends && trends.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={trends}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="correct" name="Correct" fill="#22c55e" stackId="a" />
                        <Bar dataKey="false_positive" name="False Positive" fill="#f59e0b" stackId="a" />
                        <Bar dataKey="false_negative" name="False Negative" fill="#ef4444" stackId="a" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-500">
                      No daily breakdown data yet
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Signals Tab */}
          <TabsContent value="signals" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Signal Performance Table */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Signal Performance</CardTitle>
                  <CardDescription>Accuracy metrics for each detection signal</CardDescription>
                </CardHeader>
                <CardContent>
                  {signalPerformance?.signals?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2">Signal</th>
                            <th className="text-left py-3 px-2">Type</th>
                            <th className="text-center py-3 px-2">Total</th>
                            <th className="text-center py-3 px-2">Correct</th>
                            <th className="text-center py-3 px-2">FP</th>
                            <th className="text-center py-3 px-2">FN</th>
                            <th className="text-center py-3 px-2">Accuracy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {signalPerformance.signals.slice(0, 15).map((signal, idx) => (
                            <tr key={idx} className="border-b hover:bg-gray-50">
                              <td className="py-3 px-2 font-medium">{signal.signal.replace(/_/g, ' ')}</td>
                              <td className="py-3 px-2">
                                <Badge variant="outline" className={signal.type === 'fraud' ? 'border-red-300 text-red-700' : 'border-blue-300 text-blue-700'}>
                                  {signal.type}
                                </Badge>
                              </td>
                              <td className="text-center py-3 px-2">{signal.total}</td>
                              <td className="text-center py-3 px-2 text-green-600">{signal.correct}</td>
                              <td className="text-center py-3 px-2 text-amber-600">{signal.false_positive}</td>
                              <td className="text-center py-3 px-2 text-red-600">{signal.false_negative}</td>
                              <td className="text-center py-3 px-2">
                                <Badge className={signal.accuracy >= 80 ? 'bg-green-100 text-green-800' : signal.accuracy >= 60 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}>
                                  {signal.accuracy}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No signal performance data yet. Data will appear after admin decisions.
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Problematic Signals */}
              {signalPerformance?.problematic_signals?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-amber-500" />
                      Problematic Signals
                    </CardTitle>
                    <CardDescription>Signals with high error rates (&lt;70% accuracy)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {signalPerformance.problematic_signals.map((signal, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                          <div>
                            <p className="font-medium">{signal.signal.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-gray-500">{signal.total} uses, {signal.false_positive} FP</p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-800">{signal.accuracy}% accuracy</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Reliable Signals */}
              {signalPerformance?.reliable_signals?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                      Reliable Signals
                    </CardTitle>
                    <CardDescription>Signals with high accuracy (≥90%)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {signalPerformance.reliable_signals.map((signal, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                          <div>
                            <p className="font-medium">{signal.signal.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-gray-500">{signal.total} uses, {signal.correct} correct</p>
                          </div>
                          <Badge className="bg-green-100 text-green-800">{signal.accuracy}% accuracy</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-yellow-500" />
                  Improvement Recommendations
                </CardTitle>
                <CardDescription>AI-generated suggestions to improve model accuracy</CardDescription>
              </CardHeader>
              <CardContent>
                {recommendations?.recommendations?.length > 0 ? (
                  <div className="space-y-4">
                    {recommendations.recommendations.map((rec, idx) => (
                      <div 
                        key={idx} 
                        className={`p-4 rounded-lg border-l-4 ${
                          rec.priority === 'critical' ? 'border-l-red-500 bg-red-50' :
                          rec.priority === 'high' ? 'border-l-orange-500 bg-orange-50' :
                          rec.priority === 'medium' ? 'border-l-yellow-500 bg-yellow-50' :
                          'border-l-green-500 bg-green-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={getPriorityColor(rec.priority)}>
                                {rec.priority}
                              </Badge>
                              <Badge variant="outline">{rec.type}</Badge>
                            </div>
                            <h4 className="font-semibold text-gray-900">{rec.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                            {rec.impact && (
                              <p className="text-sm text-gray-500 mt-2">
                                <span className="font-medium">Expected Impact:</span> {rec.impact}
                              </p>
                            )}
                            {rec.current_value !== undefined && rec.recommended_value !== undefined && (
                              <div className="flex items-center gap-4 mt-3">
                                <span className="text-sm">
                                  Current: <strong>{rec.current_value}</strong>
                                </span>
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-green-600">
                                  Recommended: <strong>{rec.recommended_value}</strong>
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-600">No recommendations at this time.</p>
                    <p className="text-sm text-gray-500 mt-1">The AI model is performing within acceptable parameters.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Summary Stats */}
            {recommendations?.summary && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Analysis Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-2xl font-bold text-gray-900">{recommendations.summary.total_feedback}</p>
                      <p className="text-sm text-gray-500">Total Feedback</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-2xl font-bold text-amber-600">{recommendations.summary.false_positives}</p>
                      <p className="text-sm text-gray-500">False Positives</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{recommendations.summary.false_negatives}</p>
                      <p className="text-sm text-gray-500">False Negatives</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Feedback</CardTitle>
                <CardDescription>Latest admin decisions vs AI predictions</CardDescription>
              </CardHeader>
              <CardContent>
                {recentFeedback.length > 0 ? (
                  <div className="space-y-3">
                    {recentFeedback.map((feedback, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className={getFeedbackTypeColor(feedback.feedback_type)}>
                              {feedback.feedback_type?.replace(/_/g, ' ')}
                            </Badge>
                            <span className="text-sm text-gray-500">Job: {feedback.job_id}</span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(feedback.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                          <div>
                            <p className="text-gray-500">AI Suggested</p>
                            <p className="font-medium">{feedback.ai_suggested_action}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Admin Decision</p>
                            <p className="font-medium">{feedback.admin_action}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Fraud Score</p>
                            <p className="font-medium">{feedback.fraud_score}/100</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Compliance Score</p>
                            <p className="font-medium">{feedback.compliance_score}/100</p>
                          </div>
                        </div>
                        {feedback.fraud_signals?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-gray-500 mb-1">Signals:</p>
                            <div className="flex flex-wrap gap-1">
                              {feedback.fraud_signals.slice(0, 5).map((signal, i) => (
                                <Badge key={i} variant="outline" className="text-xs">
                                  {signal.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {feedback.admin_notes && (
                          <p className="text-sm text-gray-600 mt-2 italic">"{feedback.admin_notes}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No feedback recorded yet. Feedback is automatically captured when admins make moderation decisions.
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AIAccuracyDashboard;
